import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bed, Building2, MapPin, Plus, Ruler } from 'lucide-react';
import type { SiteListingRow } from '@/hooks/data/useSiteListings';
import { apiAssetUrl } from '@/lib/api';
import { formatMoney, formatNumber } from '@/lib/formatLocale';

export function listingLocation(l: SiteListingRow): string {
  const place = [l.plz, l.ort].filter(Boolean).join(' ');
  return [l.street_released, place].filter(Boolean).join(', ') || l.location_hint || '—';
}

/** Eigenes Foto zuerst, dann die des Inserats (dieselbe Reihenfolge wie die Galerie). */
export function listingCover(l: SiteListingRow): string[] {
  const own = l.linked_property?.primary_image_url;
  return own ? [apiAssetUrl(own), ...l.images] : l.images;
}

export function ListingStatePill({ state }: { state: SiteListingRow['state'] }) {
  const { t } = useTranslation();
  if (state === 'linked') {
    return <span className="fm-pill fm-pill-pos">{t('properties.site.state_linked', 'Auf der Website')}</span>;
  }
  if (state === 'pending') {
    return <span className="fm-pill fm-pill-warn">{t('properties.site.state_pending', 'Prüfung ausstehend')}</span>;
  }
  return <span className="fm-pill fm-pill-gold">{t('properties.site.state_site_only', 'Nur auf der Website')}</span>;
}

interface Props {
  listing: SiteListingRow;
  canAdd: boolean;
  onOpen: () => void;
  onAdd: () => void;
}

export function SiteListingCard({ listing: l, canAdd, onOpen, onAdd }: Props) {
  const { t } = useTranslation();
  const covers = listingCover(l);
  // Fällt ein Bild aus, kommt das nächste; danach der Platzhalter.
  const [coverIndex, setCoverIndex] = useState(0);
  const cover = covers[coverIndex];
  const title = l.linked_property?.address || l.title || listingLocation(l);

  return (
    <article className="fm-card interactive overflow-hidden" onClick={onOpen}>
      <div className="fm-photo relative aspect-[16/10]">
        {cover ? (
          <img
            src={cover}
            alt={title}
            loading="lazy"
            onError={() => setCoverIndex((i) => i + 1)}
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-paper/60">
            <Building2 className="h-10 w-10" />
          </div>
        )}
        <div className="absolute end-2 top-2">
          <ListingStatePill state={l.state} />
        </div>
      </div>

      <div className="space-y-3 p-4">
        <div className="min-w-0">
          <h3 className="truncate font-display text-base text-ink">{title}</h3>
          <p className="mt-0.5 flex items-center gap-1 text-xs text-ink-3">
            <MapPin className="h-3 w-3 shrink-0" />
            <span className="truncate">{listingLocation(l)}</span>
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-2">
          {l.area != null && (
            <span className="inline-flex items-center gap-1">
              <Ruler className="h-3 w-3" />
              {formatNumber(l.area)} m²
            </span>
          )}
          {l.rooms != null && (
            <span className="inline-flex items-center gap-1">
              <Bed className="h-3 w-3" />
              {formatNumber(l.rooms)}
            </span>
          )}
          <span className="ms-auto text-sm font-semibold text-ink">
            {l.price != null ? formatMoney(l.price) : t('properties.site.price_on_request', 'Preis auf Anfrage')}
            {l.price != null && l.transaction === 'Miete' && (
              <span className="font-normal text-ink-3"> {t('properties.site.per_month', '/ Monat')}</span>
            )}
          </span>
        </div>

        <dl className="space-y-1 border-t border-line pt-2 text-xs">
          {l.linked_property && (
            <div className="flex justify-between gap-2">
              <dt className="shrink-0 text-ink-3">{t('properties.site.responsible_agent', 'Zuständiger Makler')}</dt>
              <dd className="truncate text-ink-2">{l.linked_property.assigned_agent_name ?? '—'}</dd>
            </div>
          )}
          <div className="flex justify-between gap-2">
            <dt className="shrink-0 text-ink-3">{t('properties.site.listing_contact', 'Kontakt im Inserat')}</dt>
            <dd className="truncate text-ink-2">{l.agent_name ?? '—'}</dd>
          </div>
        </dl>

        {canAdd && l.state !== 'linked' && (
          <button
            type="button"
            disabled={l.state === 'pending'}
            onClick={(e) => {
              e.stopPropagation();
              onAdd();
            }}
            className="inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-line bg-card px-3 py-1.5 text-xs font-semibold text-ink-2 hover:bg-paper-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Plus className="h-3.5 w-3.5" />
            {t('properties.site.add_to_crm', 'Ins CRM übernehmen')}
          </button>
        )}
        {canAdd && l.state === 'pending' && (
          <p className="text-xs text-ink-3">
            {t(
              'properties.site.pending_hint',
              'Für dieses Inserat gibt es einen Vorschlag zu einem bestehenden Objekt. Die Administration prüft ihn; bis dahin ist das Hinzufügen gesperrt.',
            )}
          </p>
        )}
      </div>
    </article>
  );
}
